import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import ComboBox from 'Components/ComboBox'
import { makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import { useNavigate } from 'react-router-dom'
import { setSiteUser } from 'reduxLib/slices/userSlice' // LAP-754
import {
  ModernDialogActions,
  ModernDialogContent,
  primaryActionSx,
  QuestionBlock,
  questionnaireDialogSx,
  questionnairePaperSx,
  QuestionnaireTitle,
  secondaryActionSx,
} from '../QuestionnaireDialogFrame'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
export default function QuestionAnswerFormRiskProfile({ oldData, updateReports, setOpenRiskProfile }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  let w_fprofile = sessionStorage.getItem('w-fprofile')
  const navigate = useNavigate()
  const dispatch = useDispatch() // LAP-754 — refresh siteUser after submit
  const [questionanswer, setQuestionanswer] = useState([])
  const [userAge, setUserAge] = useState(null)

  const [open, setOpen] = useState(true)

  const { handleSubmit, reset, setValue } = useForm({})

  useEffect(() => {
    fetchQuestionanswer()
    fetchUserProfile()
  }, [])

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    reset()
    setOpenRiskProfile(false)
  }

  const onSubmit = async (data) => {
    const payload = {
      ans: [],
    }
    Object.keys(data).forEach((v, e) => {
      payload.ans.push(parseInt(data[v].id))
    })

    const response = await makePostRequest(urls.RiskProfileQuestionary, payload, {}, true)
    if (response.status === 200) {
      if (w_fprofile) {
        // LAP-754 — store the result for the wizard's FinanceProfile popup,
        // refresh siteUser so the wizard's step picker has the latest
        // user_wizard state (otherwise App.js may render a stale step and
        // skip the result dialog), then navigate back to the wizard.
        sessionStorage.removeItem('w-fprofile')
        sessionStorage.setItem('w-fprofile-result', response.data)
        localStorage.setItem('w-fprofile-done', true)
        try {
          // Force-refresh the current user payload so Redux re-evaluates
          // which wizard step should render next.
          const userResp = await makeGetRequest('/api/user/user/', {}, true)
          if (userResp?.data) {
            dispatch(setSiteUser(userResp.data))
          }
        } catch (e) {
          // best-effort: don't block navigation on refresh failure
        }
        navigate('/')
      }
      updateReports()
      handleClose()
    }
  }

  const fetchQuestionanswer = async () => {
    const url = urls.RiskProfileQuestionary
    const { data, status } = await makeGetRequest(url, {}, true)
    if (status === 200) {
      setQuestionanswer(data)
    }
  }

  const calculateAge = (birthDate) => {
    if (!birthDate) return null
    const parts = birthDate.split('-')
    const normalizedBirthDate =
      parts.length === 3 && parts[0].length === 2
        ? `${parts[2]}-${parts[1]}-${parts[0]}`
        : birthDate
    const birthDateObj = new Date(normalizedBirthDate)
    if (Number.isNaN(birthDateObj.getTime())) return null

    const today = new Date()
    let age = today.getFullYear() - birthDateObj.getFullYear()
    const monthDifference = today.getMonth() - birthDateObj.getMonth()
    const dayDifference = today.getDate() - birthDateObj.getDate()

    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--
    }

    return age
  }

  const fetchUserProfile = async () => {
    const response = await makeGetRequest('/api/user/user/', {}, true)
    if (response.status === 200) {
      setUserAge(calculateAge(response.data.date_de_naissance))
    }
  }

  const getAnswerPoint = (answer) => {
    const match = answer?.label?.match(/^[a-e]\)/i)
    if (!match) return null
    return match[0].toLowerCase().charCodeAt(0) - 96
  }

  const getAutoAnswer = (question) => {
    // LAP-754 — match age / experience questions across all supported
    // locales (fr/en/de/it/es) so the regression where age and knowledge
    // stopped auto-filling because the wizard switched language is fixed.
    const normalizedQuestion = question?.question?.toLowerCase() || ''

    const AGE_TOKENS = ['âge', 'age', 'alter', 'edad', 'età', 'eta']
    const isAgeQuestion = AGE_TOKENS.some((tok) => normalizedQuestion.includes(tok))
    if (isAgeQuestion && Number.isFinite(userAge)) {
      let point = 5
      if (userAge < 25) point = 1
      if (userAge >= 25 && userAge <= 35) point = 2
      if (userAge >= 36 && userAge <= 45) point = 3
      if (userAge >= 46 && userAge <= 60) point = 4
      return question.ans.find((answer) => getAnswerPoint(answer) === point)
    }

    const KNOWLEDGE_TOKENS = [
      'expérience en matière',
      'experience en matiere',
      'experience',
      'erfahrung',
      'esperienza',
      'experiencia',
      'knowledge',
      'connaissance',
      'conoscenza',
      'conocimiento',
      'kenntnis',
    ]
    const isKnowledgeQuestion = KNOWLEDGE_TOKENS.some((tok) => normalizedQuestion.includes(tok))
    if (isKnowledgeQuestion) {
      const score = Number.parseInt(oldData?.financiere_val, 10)
      const scoreLabel = oldData?.financiere_score
      let point = null

      if (scoreLabel === 'Questionnaire de connaissance') return null

      if (score === 0 || scoreLabel === 'Nul') point = 1
      if (score === 1 || scoreLabel === 'Faible') point = 2
      if (score === 2 || scoreLabel === 'Moyen') point = 3
      if (score === 3 || scoreLabel === 'Expert') point = 5

      return question.ans.find((answer) => getAnswerPoint(answer) === point)
    }

    return null
  }

  const SelectBox = ({ val, namev }) => {
    const [selectedVal, setSelectedVal] = useState(null)
    useEffect(() => {
      const autoAnswer = getAutoAnswer(val)
      let sf = autoAnswer ? [autoAnswer] : val.ans.filter((v) => v.selected === 1)

      if (sf.length > 0) {
        setValue(namev, sf[0])
        setSelectedVal(sf[0])
      }
    }, [val, namev, userAge, oldData])

    const isAutoAnswered = Boolean(getAutoAnswer(val))

    return (
      <>
        <ComboBox
          handleAutoComplete={(e, j) => {
            if (isAutoAnswered) return
            setValue(namev, j)
            setSelectedVal(j)
          }}
          data={val.ans}
          name={namev}
          required={true}
          selectedValue={selectedVal}
          disabled={isAutoAnswered}
          sx={{ width: '100%', mt: 1 }}
        />
      </>
    )
  }
  const totalQuestions = questionanswer?.length || 0

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        className="no-drag"
        PaperProps={{ sx: questionnairePaperSx }}
        sx={questionnaireDialogSx}
      >
        <QuestionnaireTitle
          eyebrow={t('monPatrimoine.investorProfileEyebrow')}
          title={t('monPatrimoine.riskProfileTitle')}
          subtitle={t('monPatrimoine.riskProfileSubtitle')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModernDialogContent>
            {questionanswer &&
              questionanswer.map((val, idx) => (
                <QuestionBlock key={idx} index={idx} total={totalQuestions} question={val.question}>
                  <SelectBox namev={'qv' + idx} val={val} />
                </QuestionBlock>
              ))}
          </ModernDialogContent>
          <ModernDialogActions>
            <Button variant="contained" type="submit" sx={primaryActionSx}>{t('monPatrimoine.validate')}</Button>
            <Button
              sx={secondaryActionSx}
              onClick={() => {
                handleClose()
                if (w_fprofile) {
                  sessionStorage.removeItem('w-fprofile')
                  navigate('/')
                }
              }}
            >
              {t('monPatrimoine.cancel')}
            </Button>
          </ModernDialogActions>
        </form>
      </Dialog>
    </>
  )
}
